
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Check, Calendar, Clock, MapPin, User, ArrowRight } from 'lucide-react';
import CaregiverTracker from '@/components/CaregiverTracker';

interface BookingDetails {
  id: string;
  service: string;
  date: string;
  time: string;
  duration: string;
  recipientName: string;
  recipientAddress: string;
  specialInstructions?: string;
  caregiver?: string;
  caregiverId?: string;
  status: string;
}

const BookingConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);
  const [showTracker, setShowTracker] = useState(false);
  
  useEffect(() => {
    // Retrieve booking details from localStorage
    const storedDetails = localStorage.getItem('bookingDetails');
    if (storedDetails) {
      setBookingDetails(JSON.parse(storedDetails));
    } else {
      // If no booking details found, use a fallback
      setBookingDetails({
        id: "BKG-" + Math.floor(10000 + Math.random() * 90000),
        service: "Home Care",
        date: "May 28, 2023",
        time: "10:00 AM",
        duration: "2 hours",
        recipientName: "Unknown",
        recipientAddress: "123 Main Street, Anytown, CA 91234",
        caregiver: "Sarah Johnson",
        caregiverId: "cg-123",
        status: "Processing"
      });
    }
    
    // Simulate a delay before showing the tracker (as if caregiver is assigned and starts moving)
    const timer = setTimeout(() => {
      setShowTracker(true);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);

  if (!bookingDetails) {
    return (
      <Layout>
        <div className="pt-32 pb-20 flex justify-center">
          <div className="loader"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Booking Confirmation | CareConnect</title>
        <meta name="description" content="Your booking has been confirmed. View your booking details and status." />
      </Helmet>

      <section className="pt-32 pb-20 bg-gradient-to-br from-care-50 to-white">
        <div className="container-custom">
          <motion.div
            className="max-w-2xl mx-auto text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
              Booking Confirmed!
            </h1>
            
            <p className="text-lg text-muted-foreground mb-6">
              Your service request has been submitted and will be assigned to a caregiver shortly.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-6 mb-10">
            <motion.div
              className="glass-morphism rounded-xl p-6 md:col-span-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="border-b border-gray-200 pb-4 mb-4">
                <h2 className="text-xl font-semibold text-care-800">Booking Details</h2>
                <p className="text-sm text-muted-foreground">Booking ID: {bookingDetails.id}</p>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-start">
                    <div className="p-2 bg-care-50 rounded-lg text-care-600 mr-3">
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium text-care-800">{bookingDetails.service}</p>
                      <p className="text-sm text-muted-foreground">{bookingDetails.date}</p>
                    </div>
                  </div>
                  <div className="bg-amber-50 text-amber-800 text-sm py-1 px-3 rounded-full">
                    {bookingDetails.status}
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="p-2 bg-care-50 rounded-lg text-care-600 mr-3">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium text-care-800">Recipient</p>
                    <p className="text-sm text-muted-foreground">{bookingDetails.recipientName}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="p-2 bg-care-50 rounded-lg text-care-600 mr-3">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium text-care-800">Time</p>
                    <p className="text-sm text-muted-foreground">{bookingDetails.time} ({bookingDetails.duration})</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="p-2 bg-care-50 rounded-lg text-care-600 mr-3">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium text-care-800">Location</p>
                    <p className="text-sm text-muted-foreground">{bookingDetails.recipientAddress}</p>
                  </div>
                </div>

                {bookingDetails.specialInstructions && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-care-800 mb-1">Special Instructions</p>
                    <p className="text-sm text-muted-foreground">{bookingDetails.specialInstructions}</p>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-medium text-care-800 mb-2">Caregiver Assignment</h3>
                
                {bookingDetails.caregiver ? (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-sm text-blue-800 mb-2">
                      Your booking has been assigned to <span className="font-medium">{bookingDetails.caregiver}</span>
                    </p>
                    <Link 
                      to={`/caregiver/${bookingDetails.caregiverId || 'cg-123'}`}
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View caregiver profile <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      You will receive a notification when a caregiver has been assigned to your booking.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
            
            {showTracker && bookingDetails.caregiver && (
              <motion.div 
                className="md:col-span-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <CaregiverTracker 
                  caregiverId={bookingDetails.caregiverId || "cg-123"}
                  caregiverName={bookingDetails.caregiver}
                  caregiverImage="https://source.unsplash.com/WLUHO9A_xik/400x400"
                  estimatedArrival={bookingDetails.date + " at " + bookingDetails.time}
                />
              </motion.div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/services')}
              className="border-care-200 text-care-700"
            >
              Book Another Service
            </Button>
            <Button onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BookingConfirmation;
